import {useContext, useEffect, useState, useRef} from "react";
import {Button, Container, Form} from "react-bootstrap";
import {toast} from 'react-toastify';
import { useTranslation } from "react-i18next";
import AppConfigContext from "../context/AppConfigContext";
import APP_ENV from "../config/appEnv";
import FormTitle from "../components/FormTitle";
import FormStep from "../components/FormStep";
import Icon from "../components/Icon";
import {
    checkValid, getFormattedValue, getProgramTypeFromSubdomain, getStateFromSubdomain, isDateSameOrBefore,
    isEmpty,
    isPhoneNumberValid, pickProps,
    scrollToTop,
    toDate,
} from "../utils/utils";
import {RECORD_STATUS, VALIDATIONS, SUBMISSION_TYPE, VALUE, US_STATE, PROGRAM_TYPE} from "../utils/constants";
import applicationApi from "../services/api/applicationService";
import {getFormFields} from "../components/default/fields";
import RadioField from "../components/RadioField";
import Loader from "../components/Loader";
import styles from "./EntryForm.module.scss";
import CharacterCounter from "../components/CharacterCounter";

const EntryForm = () => {
    const state = getStateFromSubdomain();
    const programType = getProgramTypeFromSubdomain();

    const formRef = useRef(null);
    const { companyId, config } = useContext(AppConfigContext);
    const { t: tHeader, i18n } = useTranslation(config.theme, { keyPrefix: "header" });
    const { t: tActions } = useTranslation(config.theme, { keyPrefix: "actions" });
    const { t: tFields } = useTranslation(config.theme, { keyPrefix: "fields" });
    const { t: tValidations } = useTranslation(config.theme, { keyPrefix: "validations" });
    const { t: tValues } = useTranslation(config.theme, { keyPrefix: "values" });
    const { t: tMessages } = useTranslation(config.theme, { keyPrefix: "messages" });

    const customHeader = config.entryForm?.customHeader;
    let CustomHeader = null;
    if (customHeader?.enabled) {
        CustomHeader = customHeader.component;
    }

    const [currentStep, setCurrentStep] = useState(1);
    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [startTime, setStartTime] = useState(new Date());
    const [states, setStates] = useState(null);
    const [step1Fields, setStep1Fields] = useState([]);
    const [step2Fields, setStep2Fields] = useState([]);
    const [allFields, setAllFields] = useState([]);

    const getStates = async () => {
        return await applicationApi.GetStatesByCompanyId(companyId, programType).then((response) => {
            const { data } = response;
            // Trim every entry and keep only known state codes, so a blank, a doubled comma or a
            // placeholder such as ZZ never becomes an unlabeled state option.
            const companyStatesList = data?.stateList?.split(',').map(code => code.trim()).filter(code => US_STATE[code]);
            const stateCode = state?.toUpperCase();
            const stateInUsState = stateCode && US_STATE[stateCode];

            // A non-default program type has no fallback group, so an empty list means no state is
            // open for it: offer none rather than every US state.
            if (programType !== PROGRAM_TYPE.DSNP && !(companyStatesList?.length > 0)) {
                setStates({});
            } else if (companyStatesList?.length > 0) {
                if (stateInUsState && companyStatesList.includes(stateCode)) {
                    setStates({ [stateCode]: US_STATE[stateCode] });
                } else {
                    const statesObj = pickProps(US_STATE, companyStatesList);
                    setStates(statesObj);
                }
            } else {
                if (stateInUsState) {
                    setStates({ [stateCode]: US_STATE[stateCode] });
                } else {
                    setStates(US_STATE);
                }
            }
        }).catch((error) => {
            toast(`Error: ${error.response?.data?.message ?? error}`, { type: 'error' })
        });
    }

    const getStepFields = () => {
        const fieldGetter = config.entryForm?.customFields?.enabled ? config.entryForm?.customFields?.fieldGetter : getFormFields;
        const fieldsObj = config.isMultiLingual ? fieldGetter(formData, states, tFields, tValues) : fieldGetter(formData, states);
        setStep1Fields(fieldsObj.step1Fields);
        setStep2Fields(fieldsObj.step2Fields);

        setAllFields([...fieldsObj.step1Fields, ...fieldsObj.step2Fields]);
    }

    const updateValidations = () => {
        if (errors) {
            for (let name in errors) {
                validateField(name, formData[name], formData, false);
            }
        }
    }

    useEffect(() => {
        setStartTime(new Date());
        getStates();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (states) {
            getStepFields();
        }
        // eslint-disable-next-line
    }, [states, formData.noPhone, formData.phone]);

    useEffect(() => {
        const stateCode = state?.toUpperCase();
        if (states && stateCode && stateCode in states) {
            setFormData(prev => ({ ...prev, state: stateCode }));
        }
    }, [states, state]);

    useEffect(() => {
        const handleLanguageChange = () => {
            getStepFields();
            updateValidations();
            if (formRef.current) {
                formRef.current.reset(); 
                setFormData({});
                setErrors({});

                const inputs = formRef.current.querySelectorAll('.is-invalid');
                inputs.forEach(input => input.classList.remove('is-invalid'));
            }
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
        // eslint-disable-next-line
    }, [i18n, formData, states]);

    const submitApplication = async (data, form) => {
        setIsLoading(true);

        if (data?.noPhone) {
            data.phone = "xxxxxxxxxx";
            delete data.noPhone;
        }

        let communicationPreference = '';
        if (data.communicationPreference) {
            communicationPreference = Array.isArray(data.communicationPreference) ? data.communicationPreference.join(',') : data.communicationPreference;
        } else {
            communicationPreference = null;
        }

        const submitData = {
            ...data,
            communicationPreference: communicationPreference,
            birthday: toDate(data.birthday),
            startTimeDate: startTime,
            submissionType: SUBMISSION_TYPE.External,
            companyId: companyId,
            programType: programType,
            recordStatus: RECORD_STATUS.New,
            evaluationUser: 0 // we don't have any login authentication, keeping it 0 as this value is not null in database.
        }

        await applicationApi.CreateApplicationEntry(submitData)
            .then((response) => {
                toast('Application submitted successfully', { type: 'success' });

                if (window.appInsights) {
                    const appInsights = window.appInsights;
                    
                    var urlParams = new URLSearchParams(window.location.search);
                    var utmSource = urlParams.get('utm_source');
                    var utmMedium = urlParams.get('utm_medium');
                    var utmCampaign = urlParams.get('utm_campaign');
                    var utmState = urlParams.get('utm_state');
                    var utmTenant = urlParams.get('utm_tenant');

                    if (utmSource && utmMedium && utmCampaign) {
                        appInsights.trackEvent({ name: 'Form Submission: UTM Parameters', properties: { 
                            utmSource: utmSource, 
                            utmMedium: utmMedium, 
                            utmCampaign: utmCampaign,
                            utmState: utmState,
                            utmTenant: utmTenant,
                            fullName: `${data.firstName} ${data.lastName}`
                        }});
                    }
                }                

                if (step2Fields.length === 0) {
                    handleNext(3)
                } else {
                    handleNext();
                }

                // reset form after successful submit
                form.reset()
                setErrors({});
                setFormData({});
                setIsLoading(false);
                form.classList.remove('was-validated');
            })
            .catch((error) => {
                setIsLoading(false);
                toast(`Error: ${error.response?.data?.message ?? error}`, { type: 'error' })
            })
    }

    const handleSubmit = (event) => {
        const form = event.currentTarget;

        event.preventDefault();
        event.stopPropagation();

        if (form.checkValidity() === false) {
            setValidated(true);
        } else {
            const formSubmitData = new FormData(form);
            const formSubmitDataObj = {};

            for (const [key, value] of formSubmitData.entries()) {
                if (formSubmitDataObj[key]) {
                    // this 'if-else' condition is used to create an array of values from multiple checkboxes
                    if (Array.isArray(formSubmitDataObj[key])) {
                        formSubmitDataObj[key].push(value);
                    } else if (formSubmitDataObj[key] !== value && value !== "") {
                        // this condition is specifically for customRender of radio checkbox, otherwise we can just use 'else' instead of 'else if'
                        // without the condition the value will be an array when radio is selected and textbox value is blank
                        formSubmitDataObj[key] = [formSubmitDataObj[key], value];
                    }
                } else {
                    formSubmitDataObj[key] = value !== "" ? value : null;
                }
            }

            submitApplication(formSubmitDataObj, form);
        }
    }

    const validateField = (name, value, formData, showCustomError = false) => {
        const noPhoneChecked = Array.isArray(formData.noPhone) && formData.noPhone.includes('noPhoneNumber');
        let field = allFields.find(f => f.name === name);
        let inputEl = '';
        if (field.type === "radio" || field.type === "checkbox") {
            inputEl = document.querySelector(`[data-for=${name}]`);
        } else {
            inputEl = document.querySelector(`#${name}`);

        }
        let inputLabel = config.isMultiLingual ? tFields(field.name) : field.label;
        let errorMessage = "";
        if (showCustomError) {
            const hasPhone = !isEmpty(value);

            if (!hasPhone && !noPhoneChecked) {
                errorMessage = tValidations("noPhoneNumber");
                inputEl?.classList.add('is-invalid');
            } else {
                inputEl?.classList.remove('is-invalid');
            }
        } else {
            if (isEmpty(value)) {
                if (field.isRequired) {
                    errorMessage = config.isMultiLingual ? tValidations("required", { fieldName: inputLabel }) : `${inputLabel} is required`;
                    if (field.name === 'firstName' && config.isMultiLingual) {
                        errorMessage = tValidations("requiredFirstName");
                    }
                    inputEl.classList.add('is-invalid');
                } else {
                    errorMessage = ''
                    inputEl?.classList.remove('is-invalid');
                }
            } else {
                if (field.type === 'tel') {
                    if (!noPhoneChecked && !isPhoneNumberValid(value)) {
                        const phoneValid = VALIDATIONS.Number.isPhone;
                        errorMessage = config.isMultiLingual ? tValidations(field.name) : phoneValid.msg;
                        inputEl.classList.add('is-invalid');
                    } else {
                        inputEl.classList.remove('is-invalid');
                    }
                }
                if (field.type === 'email') {
                    let emailValid = checkValid(value, VALIDATIONS.String.isEmail);
                    if (!emailValid.valid) {
                        errorMessage = config.isMultiLingual ? tValidations(field.name) : emailValid.error;
                        inputEl.classList.add('is-invalid');
                    } else {
                        inputEl.classList.remove('is-invalid');
                    }
                }
                if ((['text', 'number', 'textarea'].includes(field.type)) && !isEmpty(value)) {
                    if (field.valid !== undefined) {
                        let fieldValid = checkValid(value, field.valid);
                        if (!fieldValid.valid) {
                            errorMessage = config.isMultiLingual ? tValidations(field.name) : fieldValid.error;
                            inputEl.classList.add('is-invalid');
                        } else {
                            inputEl.classList.remove('is-invalid');
                        }
                    } else {
                        errorMessage = '';
                        inputEl.classList.remove('is-invalid');
                    }
                }
                if (field.type === 'date' && field.maxValue) {
                    const dateCompare = isDateSameOrBefore(value, field.maxValue);
                    if (!dateCompare) {
                        errorMessage = config.isMultiLingual ? tValidations("date", { fieldName: inputLabel }) : `Please choose a valid ${inputLabel}`;
                        inputEl.classList.add('is-invalid');
                    } else {
                        errorMessage = '';
                        inputEl.classList.remove('is-invalid');
                    }
                }
                }
            }

        // specific validation for custom rendered fields
        if (field.type === 'radio' && field.hasCustomRender && [...field.customRenders, ''].includes(value)) {
            inputEl.classList.add('is-invalid');
            errorMessage = config.isMultiLingual ? tValidations(field.name) : 'The Other field should not be empty';
        }

        if (errorMessage === "") {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        } else {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: errorMessage }));
        }
    }

    const handleTextChange = (name, value) => {
        setFormData((data) => ({ ...data, [name]: value }));
        validateField(name, value, formData, false);
    }

    const handleInputChange = (e) => {
        let { name, value, type, checked } = e.target;
        let valueCondition;
        let parentEl = e.target.closest(".step > div");

        let inputValue = value;

        // handle checkbox values
        let checkValues = [];
        if (type === 'checkbox') {
            let checkBoxes = document.querySelectorAll(`[name=${name}]`);
            for (let checkBox of checkBoxes) {
                if (checkBox.checked) {
                    checkValues.push(checkBox.value);
                }
            }
            if (!checked) value = "";
        }

        inputValue = (type === 'checkbox') ? checkValues : value;

        setFormData((data) => ({ ...data, [name]: inputValue }));
        valueCondition = (value !== "");

        const newValue = type === "checkbox" ? Array.from(document.querySelectorAll(`[name=${name}]`))?.filter((el) => el.checked)?.map((el) => el.value) : value;

        const updatedFormData = { ...formData, [name]: newValue };

        // Loop through all fields that depend on the current field
        allFields.forEach((field) => {
            if (field.dependsOn === name) {
                const controllingValue = updatedFormData[name];
                const shouldDisableField = Array.isArray(controllingValue)
                    ? controllingValue.includes(field.dependsValue)
                    : controllingValue === field.dependsValue;

                if (shouldDisableField) {
                    // If condition matches and field should be disabled, reset its value
                    setFormData((data) => ({ ...data, [field.name]: null }));
                }

                const effectiveValue = shouldDisableField ? 'dummy' : formData[field.name];
                validateField(field.name, effectiveValue, updatedFormData, false);
            }
        });



        // To remove validation marks when blank
        if (valueCondition) {
            parentEl.classList.remove("initial");
            parentEl.classList.add("has-value");
        } else {
            parentEl.classList.add("initial");
            parentEl.classList.remove("has-value");
        }

        validateField(name, value, updatedFormData, false);
    }

    const handleReturn = () => {
        const returnURL = APP_ENV.CLIENT_RETURN_URL;
        if (!returnURL) {
            return;
        }
        window.location.replace(returnURL);
        setTimeout(() => scrollToTop, 1000);
    }

    const handleCancel = (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleReturn();
    }

    const handleNext = (step) => {
        setCurrentStep(step ?? currentStep + 1);
        scrollToTop();
    }

    const handleBack = () => {
        setCurrentStep(currentStep - 1);
        scrollToTop();
    }
    
    const renderFormControl = (item) => {
        let { type, name, label, placeholder, optionValues, minLength, maxLength, minValue, maxValue, disabled, hidden, conditional, isRequired, dependsOn, dependsValue, hideLabel = false, removeSpacing = false, showCustomError, showCharCounter } = item;

        const renderTextField = () => {
            let inputValue = getFormattedValue(formData[name], type);

            let formHTML = <Form.Control
                    disabled={disabled}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    value={inputValue}
                    minLength={minLength ? minLength : null}
                    maxLength={maxLength ? maxLength : null}
                    min={minValue ? getFormattedValue(maxValue, type, VALUE.Min) : null}
                    max={maxValue ? getFormattedValue(maxValue, type, VALUE.Max) : null}
                    id={name}
                    required={isRequired}
                    onChange={handleInputChange}             
                    onBlur={() => {
                        if (name === 'phone') {
                            validateField(name, formData[name], formData, showCustomError);
                        }
                    }}
                />

            let keyWithPrefix = "fields.birthdayPlaceholder";
            const placeholderText = placeholder === keyWithPrefix ? null : placeholder;
            if (type === 'date' && placeholderText) {
                formHTML = (
                    <Form.Floating>
                        {formHTML}
                        <label htmlFor="floatingDate">{placeholder}</label>
                    </Form.Floating>
                );
            }

            return formHTML;
        }

        const renderOptionsField = () => (
            <Form.Select
                id={name}
                disabled={disabled}
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                required={isRequired}
            >
                {optionValues?.map((optionInfo) => {
                    return (
                        <option
                            key={optionInfo.id}
                            value={optionInfo.id}>
                            {optionInfo.value}
                        </option>
                    );
                })}
            </Form.Select>
        )

        const renderTextArea = () => (
            <>
                <Form.Control
                    placeholder={placeholder}
                    id={name}
                    as="textarea"
                    rows={3}
                    required={isRequired}
                    minLength={minLength ? minLength : null}
                    maxLength={maxLength ? maxLength : null}
                    value={formData[name] ?? ''}
                    onChange={handleInputChange}
                    disabled={disabled}
                    name={name}
                />
                {showCharCounter && (
                    <CharacterCounter
                        currentValue={formData[name]}
                        maxLength={maxLength}
                    />
                )}
            </>
        )

        const renderRadioCheckboxField = () => {
            const isChecked = (val) => {
                let checked;
                if (type === 'radio') {
                    checked = val === formData[name];
                }
                if (type === 'checkbox') {
                    checked = formData[name] ? formData[name].includes(val) : false
                }
                return checked;
            }

            return (
                <>
                    {item?.optionValues?.map((option) => {
                        if (option.customRender) {
                            return (
                                <RadioField
                                    type={type}
                                    name={name}
                                    key={option.id}
                                    value={option.value}
                                    required={isRequired}
                                    disabled={disabled}
                                    option={option}
                                    onRadioChange={handleInputChange}
                                    onTextChange={handleTextChange}
                                />
                            )
                        }
                        const isNoPhoneCheckbox = name === "noPhone" && option.value === "noPhoneNumber";

                        return (
                            <Form.Check
                                type={type}
                                name={name}
                                key={option.id}
                                label={option.label}
                                id={`${name}-${option.id}`}
                                required={isRequired}
                                disabled={disabled}
                                value={option.value}
                                onChange={handleInputChange}
                                checked={isChecked(option.value)}
                                className={isNoPhoneCheckbox ? styles.customCheckboxLabel : ''}
                            />
                        );
                    })}
                </>
            );
        }

        const renderFormField = () => {
            switch (type) {
                case "options":
                    return renderOptionsField();
                case "textarea":
                    return renderTextArea();
                case "radio":
                case "checkbox":
                    return renderRadioCheckboxField();
                default:
                    return renderTextField();
            }
        }

        let errorMessage = errors[name];
        let formGroupClass = '';
        if (hidden) {
            formGroupClass = ' d-none';
            if (conditional) {
                formGroupClass = !dependsValue.includes(formData[dependsOn]) ? ' d-none' : ''
            }
        }

        if (type === 'section') {
            // Check if next field is a subheader to reduce bottom margin
            const currentFields = currentStep === 1 ? step1Fields : step2Fields;
            const currentIndex = currentFields.findIndex(f => f.name === name);
            const nextField = currentFields[currentIndex + 1];
            const bottomMargin = nextField?.type === 'subheader' ? 'mb-1' : 'mb-3';
            return (
                <h3 className={`${bottomMargin} ${item.customClass}`}>{label}</h3>
            )
        }

        if (type === 'subheader') {
            // Prepend red asterisk to translated text
            return (
                <p className={item.customClass || ''}>
                    <span className="text-danger">* </span>
                    {label}
                </p>
            )
        }

        return (
            <Form.Group className={`${removeSpacing ? 'mb-1' : 'mb-4'} ${isRequired === true ? ' required' : ''}${formGroupClass}`}>
                {!hideLabel && (
                    <Form.Label
                        data-for={(type === "radio" || type === "checkbox") ? name : null}>
                        {label}
                    </Form.Label>
                 )}
                {renderFormField()}
                {errorMessage && (
                    <Form.Control.Feedback type="invalid">
                        {errorMessage}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
        )
    }

    const handleNextDisable = (stepFields) => {
        const isValid = stepValidation(stepFields);
        return isValid && Object.keys(errors)?.length === 0
    }

    const handleSubmitDisable = () => {
        let isValid = true;
        if (step2Fields.length === 0) {
            isValid = stepValidation(step1Fields);
        }
        return !isLoading && isValid && Object.keys(errors)?.length === 0
    }

    const stepValidation = (fields) => {
        const requiredFields = fields.filter(field => field.isRequired);
        let isStepValid = [];
        if (formData != null && requiredFields.length > 0) {
            for (let field of requiredFields) {
                const value = formData[field.name];
                isStepValid.push(value !== null && value !== '' && value !== undefined);
            }
            return isStepValid.every((value) => value === true);
        }
        return false;
    }

    const renderThankYouMessage = () => {
        let thankYouMessage = config.entryForm?.formSuccess?.message ?? 'Thank you for submitting your application. We will contact you in the next few days.';
        return config.isMultiLingual ? tMessages("thankYou") : thankYouMessage
    }

    return (
        <Container className={`px-0 ${styles.wrapper}`}>
            <FormTitle
                bgVariant={config.entryForm?.titleBg && config.entryForm.titleBg}
                title={config.isMultiLingual ? (tHeader("title") || 'Member Experience Panel Application') : config.entryForm.title} />
            {customHeader?.enabled ? <CustomHeader step={currentStep} /> : null}
            <div className={`${styles.formBackground} p-4 mb-4 position-relative ${styles.formWrapper}`}>
                {allFields.length === 0 ?
                    (<Loader />)
                        :
                    (<Form ref={formRef} noValidate validated={validated} onSubmit={handleSubmit}>
                        {currentStep === 2 && (
                            <Button variant="outline-dark" size="sm" className="d-flex align-items-center mb-4" onClick={handleBack}>
                                <Icon icon="angle-left-solid" size="12" className={styles.icon} />
                                {config.isMultiLingual ? tActions("back") : 'Back'}
                            </Button>
                        )}
                        <FormStep step={1} currentStep={currentStep}>
                            {step1Fields.map((field) => {
                                return (
                                    <div key={field.name} data-id={field.name} className="initial">
                                        {renderFormControl(field)}
                                    </div>
                                )
                            })}
                        </FormStep>
                        {step2Fields.length > 0 &&
                            <FormStep step={2} currentStep={currentStep}>
                                {step2Fields.map((field) => {
                                    return (
                                        <div key={field.name} data-id={field.name} className="initial">
                                            {renderFormControl(field)}
                                        </div>
                                    )
                                })}
                            </FormStep>
                        }
                        <FormStep step={3} currentStep={currentStep}>
                            <div className={`bg-white p-3 p-lg-5 pb-lg-4 m-3 my-lg-5 mx-auto text-center rounded-3 ${styles.messageBox}`}>
                                <p>{renderThankYouMessage()}</p>
                                {config.entryForm?.showReturn && (
                                    <Button type="submit" className="px-4 mt-2" onClick={handleReturn}>{config.isMultiLingual ? tActions("return") : "Return"}</Button>
                                )}
                            </div>
                        </FormStep>
                        <div className={`d-flex justify-content-center gap-3 mt-4 ${styles.actions}`}>
                            {(currentStep !== 3 && config.entryForm?.showCancel) && <Button variant="outline-dark" className="px-4" onClick={handleCancel}>{config.isMultiLingual ? tActions("cancel") : 'Cancel'}</Button>}
                            {(currentStep === 1 && step2Fields.length > 0) && <Button className="px-4" onClick={() => handleNext()} disabled={!handleNextDisable(step1Fields)}>{config.isMultiLingual ? tActions("next") : 'Next'}</Button>}
                            {((currentStep === 2 && step2Fields.length > 0) ||
                                    (currentStep === 1 && step2Fields.length === 0)) &&
                                <Button type="submit" className={`px-4 ${config.isPlainButton ? 'rounded-0' : ''}`} disabled={!handleSubmitDisable()}>{config.isMultiLingual ? tActions("submit") : 'Submit'}</Button>}
                        </div>
                    </Form>
                )}
            </div>
        </Container>
    )
}

export default EntryForm;
